import { gql } from '@apollo/client'
import { logQuery } from 'src/utils/apollo'
import { Breadcrumb, Col } from 'antd'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'src/utils/i18n'
import Bottom from 'src/components/guide/Bottom'
import Header from 'src/components/guide/Header'
import styles2 from 'styles/comparison-and-review.module.scss'
import Top from 'src/components/Blog/top'

function debounce(func, delay) {
  let timer

  return function () {
    let context = this
    let args = arguments
    timer && clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(context, args)
    }, delay)
  }
}

const Slug = ({ slug }) => {
  const { t, locale } = useTranslation('common')
  const [searchText, setSearchText] = useState('')
  const router = useRouter()

  const [html, setHtml] = useState('')
  const [anchorData, setAnchorData] = useState([])
  const [currentId, setCurrentId] = useState('')
  const lastScrollTop = useRef(0)
  const sidebar = useRef(null)
  const rightAd = useRef(null)
  const article = useRef(null)
  const [isShow, setIsShow] = useState(false)

  const [data, setData] = useState(null)
  const [data2, setData2] = useState([])

  // 客户端请求数据
  useEffect(() => {
    const fetchData = async () => {
      let local = 'en'
      if (locale && locale !== 'en') {
        if (locale === 'it-ch') local = 'it'
        else if (locale === 'jp') local = 'ja-JP'
        else if (locale === 'kr') local = 'ko-KR'
        else local = locale
      }

      // 查询当前文章
      const res = await logQuery({
        query: gql`
          query {
            tsafelyArticles(
              filters: { slug: { eq: "${slug}" } }
              locale: "${local}"
            ) {
              data {
                attributes {
                  title
                  content
                  updatedAt
                  subtitle
                  slug
                  publishedAt
                  seo {
                    metaTitle
                    metaDescription
                    keywords
                  }
                  style
                  li_meta
                  tsafely_article_author {
                    data {
                      attributes {
                        name
                        slug
                        profile_photo {
                          data {
                            attributes {
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                  tsafely_category {
                    data {
                      attributes {
                        name
                      }
                    }
                  }
                  image {
                    data {
                      attributes {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        `
      })

      const articleData = res.data.tsafelyArticles.data[0]?.attributes
      setData(articleData || false)

      if (!articleData) return

      // 查询相关推荐文章
      const res2 = await logQuery({
        query: gql`
          query {
            tsafelyArticles(
              filters: {
                not: { slug: { eq: "${slug}" } }
                tsafely_category: {
                  name: { in: ["${articleData.tsafely_category?.data?.attributes?.name || ''}"] }
                }
              }
              locale: "${local}"
              pagination: { pageSize: 5 }
            ) {
              data {
                id
                attributes {
                  title
                  slug
                }
              }
            }
          }
        `
      })

      setData2(res2.data.tsafelyArticles.data)
    }

    fetchData()
  }, [slug, locale])

  // 解析目录
  useEffect(() => {
    if (!data?.content) return
    const div = document.createElement('div')
    div.innerHTML = data.content
    let arr = []
    for (const i in div.childNodes) {
      if (div.childNodes[i].localName === 'h2' || div.childNodes[i].localName === 'h3') {
        div.childNodes[i].id = i
        div.childNodes[i].style.scrollMargin = '80px'
        arr.push({
          id: i,
          title: div.childNodes[i].outerText,
          localName: div.childNodes[i].localName
        })
      }
      setAnchorData([...arr])
      setHtml(div.innerHTML)
    }
  }, [data?.content])

  const onEnter = event => {
    if (event.key === 'Enter') {
      router.push('/guide/search?query=' + searchText)
    }
  }

  const onScroll = id => {
    const dom = document.getElementById(id)
    if (dom) {
      dom.scrollIntoView({ behavior: 'smooth' })
      setCurrentId(id)
    }
  }

  // 目录 & 广告固定
  useEffect(() => {
    const func = () => {
      const scrollPosition = window.scrollY
      const maxHeight = article.current.scrollHeight + 210 - window.innerHeight
      if (scrollPosition > 190 && scrollPosition < maxHeight) {
        rightAd.current.style.position = 'fixed'
        rightAd.current.style.top = '90px'
      } else {
        rightAd.current.style.position = 'relative'
        rightAd.current.style.top = '0'
      }
      if (anchorData.length === 0) return
      if (scrollPosition > 190 && scrollPosition < maxHeight) {
        sidebar.current.style.position = 'fixed'
        sidebar.current.style.top = '90px'
      } else {
        sidebar.current.style.position = 'relative'
        sidebar.current.style.top = '0'
      }
    }

    window.addEventListener('scroll', func)

    return () => window.removeEventListener('scroll', func)
  }, [anchorData])

  // 当前目录高亮
  useEffect(() => {
    const func = debounce(() => {
      const st = window.scrollY
      for (const item of anchorData) {
        const element = document.getElementById(item.id)
        if (element) {
          const rect = element.getBoundingClientRect()
          if (rect.top >= 80 && rect.bottom <= window.innerHeight) {
            setCurrentId(item.id)
            break
          }
        }
      }
      lastScrollTop.current = st
    }, 100)

    window.addEventListener('scroll', func)

    return () => window.removeEventListener('scroll', func)
  }, [anchorData])

  useEffect(() => {
    if (router.asPath.includes('#')) {
      const id = router.asPath.split('#')[1]
      setTimeout(() => {
        const el = document.getElementById(id)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 300)
    }
  }, [router.asPath])

  if (data === false) {
    return (
      <div className='text-center py-20'>
        <p className='text-[18px] text-[#282938] mb-4'>Page not found</p>
        <Link href='/guide' className='text-[#2a99ff] underline'>
          {t('Guide')}
        </Link>
      </div>
    )
  }

  if (!data) return <div className="text-center py-10">Loading...</div>

  return (
    <>
      <Header hiddenLang={true} />

      <main className='max-w-[1200px] Bxl:px-[16px] min-h-[calc(var(--vh)*100-380px)] Bld:min-h-[calc(var(--vh)*100-360px)] pt-[20px] pb-[40px] mx-auto'>
        <div className='w-full flex justify-between items-center'>
          <Breadcrumb
            items={[
              {
                title: <Link href='/guide'>{t('Guide')}</Link>
              },
              {
                title: data.title
              }
            ]}
          />
          <div className='w-[300px] h-[40px] bg-[#FFF] border border-[rbg(121,121,121)] rounded-[41px] outline-none px-[24px] flex items-center Bld:hidden'>
            <Image src='/images/contacts/search.svg' width={20} height={0} alt='search' />
            <input
              className='ml-[10px] flex-1 outline-none placeholder:text-[rgb(129,129,135)]'
              placeholder={t('Search')}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyPress={onEnter}
            />
          </div>
        </div>
        <div className='max-w-[1200px] pt-[60px] Bld:pt-[30px] flex justify-between Bxl:justify-center mx-auto'>
          {/* 左侧 */}
          {anchorData.length !== 0 && (
            <div className='w-[251px] Blds:Bxl:mr-[55px] Bld:hidden'>
              <div ref={sidebar} className='w-[251px] h-[calc(var(--vh)*100-100px)] flex flex-col'>
                <div className='w-full rounded-[8px] text-[16px] font-[600] text-[#000] mb-[20px]'>
                  {t('Table of contents')}
                </div>
                <div className='overflow-auto flex-1'>
                  {anchorData.map((item, i) => (
                    <Fragment key={i}>
                      <div
                        className={`${currentId === item.id ? 'text-[#000]' : 'text-[rgba(0,0,0,0.4)]'
                          } pr-[10px] cursor-pointer flex items-center`}
                        onClick={() => onScroll(item.id)}
                      >
                        {item.localName == 'h2' && (
                          <>
                            <span className='text-[15px] font-[600]'>{item.title}</span>
                          </>
                        )}
                        {item.localName == 'h3' && (
                          <>
                            <div
                              className={`${currentId === item.id ? 'bg-[#000]' : 'bg-[rgba(0,0,0,0.4)]'
                                } w-[3px] h-[3px] rounded-[50%] ml-[15px] mr-[10px]`}
                            ></div>
                            <span className='text-[14px] font-[600]'>{item.title}</span>
                          </>
                        )}
                      </div>

                      {anchorData.length > i + 1 && <div className='w-full h-[24px] pl-[24px]'></div>}
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 文章 */}
          <div ref={article} className={`${anchorData.length !== 0 ? 'smjs:max-w-[584px]' : 'smjs:max-w-[890px] Guide'} smj:w-full`}>
            <Col className={`w-full ${styles2.header}`} dangerouslySetInnerHTML={{ __html: html }}></Col>
          </div>

          {/* 右侧 */}
          <div className='w-[265px] Bxl:hidden'>
            <div ref={rightAd} className='w-[265px]'>
              <div className='w-full text-[16px] font-[600] text-[#000] mb-[20px]'>{t('Related helpful articles')}</div>
              {data2.map((item, i) => (
                <div key={i} className='mt-[20px]'>
                  <Link
                    href={'/guide/' + item.attributes.slug}
                    className='text-[15px] text-[#222] hover:text-[#2a99ff] cursor-pointer'
                  >
                    {item.attributes.title}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className='mt-[30px] pt-[30px] mx-auto max-w-[830px] Bxl:flex hidden border-t'>
          <div className='w-full'>
            <div className='w-full text-[16px] font-[600] text-[#000] mb-[20px]'>{t('Related helpful articles')}</div>
            {data2.map((item, i) => (
              <div key={i} className='mt-[20px]'>
                <Link
                  href={'/guide/' + item.attributes.slug}
                  className='text-[15px] text-[#222] hover:text-[#2a99ff] cursor-pointer'
                >
                  {item.attributes.title}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </main>
      {anchorData.length !== 0 && (
        <>
          {isShow && (
            <div
              className='w-full h-[calc(var(--vh)*100-60px)] hidden Bld:flex items-end fixed bottom-[60px] bg-[rgba(0,0,0,0.4)]'
              onClick={() => setIsShow(false)}
            >
              <div
                className='w-full py-[15px] px-[20px] rounded-t-[10px] bg-[#1c1e53] flex flex-col justify-center'
                onClick={e => {
                  e.stopPropagation()
                }}
              >
                {anchorData.map((item, i) => (
                  <Fragment key={i}>
                    <div
                      className={`${currentId === item.id ? 'text-[#FFF]' : 'text-[rgba(255,255,255,0.6)]'
                        } cursor-pointer flex items-center`}
                      onClick={() => {
                        onScroll(item.id)
                        setIsShow(false)
                      }}
                    >
                      {item.localName == 'h2' && (
                        <>
                          <span className='text-[15px]'>{item.title}</span>
                        </>
                      )}
                      {item.localName == 'h3' && (
                        <>
                          <div
                            className={`${currentId === item.id ? 'bg-[#FFF]' : 'bg-[rgba(255,255,255,0.6)]'
                              } w-[3px] h-[3px] rounded-[50%] ml-[15px] mr-[10px]`}
                          ></div>
                          <span className='text-[14px]'>{item.title}</span>
                        </>
                      )}
                    </div>

                    {anchorData.length > i + 1 && <div className='w-full h-[15px]'></div>}
                  </Fragment>
                ))}
              </div>
            </div>
          )}
          <div className='w-full h-[60px] bg-[#1c1e53] fixed bottom-0 justify-center items-center hidden Bld:flex'>
            <button
              class='w-[170px] h-[38px] bg-[#00a8ff] rounded-md text-white text-[15px] cursor-pointer'
              onClick={() => setIsShow(val => !val)}
            >
              {t('Table of contents')}
            </button>
          </div>
        </>
      )}

      <Bottom></Bottom>
      {anchorData.length !== 0 && <div className='w-full h-[60px] hidden Bld:flex'></div>}
      <Top />
    </>
  )
}

export async function getStaticPaths() {
  let paths = []
  let locales = ['en', 'de', 'es', 'fr', 'jp', 'kr', 'pt', 'it-ch']
  for (const locale of locales) {
    let category = ''
    let local = 'en'
    if (locale && locale != 'en') {
      if (locale == 'it-ch') {
        local = 'it'
        category = '-it'
      } else if (locale == 'jp') {
        local = 'ja-JP'
        category = '-' + locale
      } else if (locale == 'kr') {
        local = 'ko-KR'
        category = '-' + locale
      } else {
        local = locale
        category = '-' + locale
      }
    }
    // dashboards：实际在用的 user-guide-* / back-up-*；
    // ios-guide / android-guide：历史分类，保留以免 CMS 迁回后漏构建
    const { data } = await logQuery({
      query: gql`
        query {
          tsafelyArticles(
            filters: {
              tsafely_category: {
                name: {
                  in: [
                    "ios-guide${category}"
                    "android-guide${category}"
                    "dashboards"
                    "dashboards${category}"
                  ]
                }
              }
            }
            locale: "${local}"
            pagination: { page: 1, pageSize: 3000 }
          ) {
            data {
              id
              attributes {
                slug
              }
            }
          }
        }
      `
    })
    paths.push(
      ...data.tsafelyArticles.data.map(item => ({
        params: { slug: `${item.attributes.slug}` }
      }))
    )
  }

  // blocking：CMS 新增/改分类后无需整站重建即可访问，避免再出现整页 404
  return { paths, fallback: 'blocking' }
}

export async function getStaticProps({ params }) {
  return { props: { slug: params.slug } }
}

export default Slug